// Staff alerts as a staff-run station's time runs out (ADR-0033, N-30): at 10 and 5 minutes left
// (`sessions.staffAlertMinutes`), when the paid time is up (grace starts), and — quietly — when the session ends on its
// own. The server's checkpoint sweep raises them through the notification pipeline, which publishes `alerts.new` on
// `venue:{v}:floor`; on whatever screen of the venue console staff are, each becomes a toast, with a short chime for
// all but the end. The floor's own refetch on `alerts.new` (realtime.ts) keeps the tiles current alongside.
//
// The chime is the Web Audio API's two-note tone — no sound file to ship or cache. A browser that blocks audio until the
// page has been used, or has no Web Audio at all (a test), simply stays silent: the toast is the alert, the sound is a
// nudge.

import { ALERTS_NEW, channels } from '@clavian/contracts/constants';
import type { NotificationNewPayload, RealtimeEnvelope } from '@clavian/contracts/realtime';
import type { TypeId } from '@clavian/contracts/typeid';
import { useToast } from '@clavian/ui-web';
import { useCallback } from 'react';

import { useAccess } from '../api/queries';
import { useChannelEvents, useRealtimeLive } from '../realtime';

/** The catalogue id of a staff-run station's time alert (spec/16 §4.3). */
export const STAFF_RUN_ALERT_CODE = 'N-30';

/** A staff-run alert from an `alerts.new` frame, or `undefined` for any other frame. Pure. */
export function staffRunAlertOf(envelope: RealtimeEnvelope): NotificationNewPayload | undefined {
  if (envelope.type !== ALERTS_NEW) return undefined;
  const payload = envelope.p as Partial<NotificationNewPayload> | undefined;
  if (payload?.code !== STAFF_RUN_ALERT_CODE || typeof payload.title !== 'string') return undefined;
  return payload as NotificationNewPayload;
}

/** Two soft notes, a fourth apart. Silent where audio is unavailable or blocked. */
export function playStaffAlertChime(): void {
  try {
    const Context: typeof AudioContext | undefined =
      typeof window === 'undefined' ? undefined : (window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext);
    if (Context === undefined) return;
    const context = new Context();
    const start = context.currentTime;
    [880, 660].forEach((frequency, index) => {
      const at = start + index * 0.2;
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, at);
      gain.gain.exponentialRampToValueAtTime(0.2, at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.32);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(at);
      oscillator.stop(at + 0.34);
    });
    window.setTimeout(() => void context.close().catch(() => undefined), 1000);
  } catch {
    // Audio is a nudge, never a requirement.
  }
}

/**
 * Listen for staff-run alerts on the venue's floor channel while the socket is live, and show each as a toast — with
 * the chime when it is urgent (10 and 5 minutes left, time up). `enabled` is the member's `floor:read`, which the
 * gateway checks too.
 */
export function useStaffRunAlerts(venueId: string, enabled: boolean, chime: () => void = playStaffAlertChime): void {
  const { show } = useToast();
  const live = useRealtimeLive();
  const onEvent = useCallback(
    (envelope: RealtimeEnvelope) => {
      const alert = staffRunAlertOf(envelope);
      if (alert === undefined) return;
      show({ tone: alert.urgent ? 'warn' : 'neutral', title: alert.title, description: alert.body, durationMs: alert.urgent ? 12_000 : 6_000 });
      if (alert.urgent) chime();
    },
    [show, chime],
  );
  useChannelEvents(channels.venue(venueId as TypeId<'ven'>, 'floor'), onEvent, live && enabled);
}

/**
 * Mounts the listener inside the venue shell (under its realtime socket), so the alerts reach staff on every screen of
 * the venue console — for a member who may watch the floor.
 */
export function StaffRunAlerts({ venueId }: { readonly venueId: string }): null {
  const access = useAccess({ venueId });
  useStaffRunAlerts(venueId, access.status === 'ready' && access.can('floor:read'));
  return null;
}
